import {
	getCurrentUser,
	signInWithCustomToken,
	signInWithEmail,
	signOut,
	signUpWithEmailAndPassword
} from 'library/firebase/fb.auth.utils';
import AuthHelper from 'library/helpers/authHelper';
import { logUserActivityEvent } from 'library/helpers/Logger';
import SuperFetch from 'library/helpers/superFetch';
import { getToken } from 'library/helpers/utility';
import { call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { appActions } from 'reduxStore/app/slice';
import { defaultSettingsActions } from 'reduxStore/defaultSettings/slice';
import { organisationActions } from 'reduxStore/organisations/slice';
import { settingsActions } from 'reduxStore/settings/slice';
import { typesActions } from 'reduxStore/types/slice';
import { setupNewWorkspace } from './helpers';
import { authActions } from './slice';
const selectedProfile = (state) => state.Auth.selectedProfile;

export function* loginRequest({ payload }) {
	// login the user in with email and password
	const { email, password } = payload;

	try {
		const user = yield call(signInWithEmail, { email, password });

		if (user.user) {
			// get user token and set it in the localstorage
			const token = yield user.user.getIdToken();
			localStorage.setItem('id_token', token);

			// dispatch the login success action
			yield put(authActions.loginSuccess());
		} else {
			throw new Error('User login failed, please try again.');
		}
	} catch (error) {
		yield put(authActions.loginError(error.message));
	}
}

export function* loginSuccess() {
	// Do Actions on Login Success

	// 1: load User's all the workspaces.
	yield put(organisationActions.loadOrganisations());
}

export function* loginError() {
	// Do Actions on Login Error

	yield delay(15000); //Reset the error after 15 seconds

	// Reset the error after 15 seconds
	yield put(authActions.resetError());
}

export function* getUserProfile() {
	try {
		// Get the current user from Firebase
		const currentUser = yield call(getCurrentUser);

		// Get the workspaces from the store
		const workspaceData = yield select((state) => state.Organisations.data);

		// If the user is not logged in, logout the user
		if (!currentUser) {
			throw new Error('User not logged in');
		}

		// If the user has no permissions (not part of any org), logout the user
		if (workspaceData.length <= 0) {
			throw new Error('No Permissions Found');
		} else {
			// Get the current user permissions from the store
			const currentSelectedProfile = yield select(selectedProfile);

			// Try to get saved orgID from localStorage
			const savedOrgID = parseInt(localStorage.getItem('od'), 10);

			// Find the current workspace from the user permissions
			const savedWorkspace = savedOrgID && workspaceData.find((org) => org.orgID === savedOrgID);

			// Decide which workspace to use: selected profile, saved workspace, or first in the list
			let workspaceToDispatch;
			if (currentSelectedProfile.orgID && currentSelectedProfile.personID) {
				// Use fresh workspace data that matches current profile's orgID
				workspaceToDispatch =
					workspaceData.find((org) => org.orgID === currentSelectedProfile.orgID) || currentSelectedProfile;
			} else {
				workspaceToDispatch = savedWorkspace || workspaceData[0];
			}

			// Extract the  person profile from Database for the workspace
			const res = yield SuperFetch.get(
				`/people/${workspaceToDispatch.personID}`,
				{},
				{},
				workspaceToDispatch.orgID
			);
			if (!res.data) {
				throw new Error('Person Profile Not Found.');
			}
			const { data: newPersonProfile } = res;

			if (newPersonProfile.personID) {
				yield put(
					authActions.getUserProfileSuccess({
						selectedProfile: {
							...newPersonProfile,
							roles: workspaceToDispatch.roles,
							orgAlias: workspaceToDispatch.alias
						},
						userPermissions: workspaceData.map((workspace) => {
							return {
								personID: workspace.personID,
								orgID: workspace.orgID,
								roles: workspace.roles,
								orgAlias: workspace.alias
							};
						})
					})
				);
			} else {
				throw new Error('No Person Profile Found');
			}
		}
	} catch (error) {
		yield put(authActions.getUserProfileError(error.message));
		yield put(authActions.logout());
	}
}

// This is called when the user profile is loaded successfully
// This is called when the user switches between the profiles.
export function* getUserProfileSuccess() {
	//* Do Actions once the user profile is loaded

	// 1: Load Person Workspaces. //? this is done to ensure that the workspaces are loaded properly
	const { organisationLoading, organisationLoadingComplete } = yield select((state) => state.Organisations);

	if (!organisationLoadingComplete && !organisationLoading) {
		yield put(organisationActions.loadOrganisations());
	}

	// 2: Load all the default settings & Groups
	const {
		orgDefaultSettingsLoadingComplete,
		personDefaultSettingsLoadingComplete,
		groupSettingsLoadingComplete,

		orgDefaultSettingsLoading,
		personDefaultSettingsLoading,
		groupSettingsLoading
	} = yield select((state) => state.DefaultSettings);

	// Load the default org settings if not loaded
	if (!orgDefaultSettingsLoadingComplete && !orgDefaultSettingsLoading) {
		yield put(defaultSettingsActions.loadOrgDefaultSettings());
	}

	// Load the default person settings if not loaded
	if (!personDefaultSettingsLoadingComplete && !personDefaultSettingsLoading) {
		yield put(defaultSettingsActions.loadPersonDefaultSettings());
	}

	// Load the default Settings group if not loaded
	if (!groupSettingsLoadingComplete && !groupSettingsLoading) {
		yield put(defaultSettingsActions.loadGroupSettings());
	}
	// 3: Load the workspace & Person settings
	// ? This is done to ensure that the settings are loaded after the default settings
	// ? This also ensures that the latest updates are loaded from the database when user log in or switch profile.
	yield put(settingsActions.loadOrgSettings());
	yield put(settingsActions.loadPersonSettings());

	// 4 Load all types required for the all the Dashboards.
	yield put(typesActions.loadAllTypes());
}

export function* logout() {
	// Logout the user

	// Clear the local storage
	localStorage.removeItem('id_token');
	localStorage.removeItem('od');

	// Sign out the user
	yield call(signOut);
}

export function* switchProfile({ payload: orgID }) {
	try {
		// Get the workspaces from the store
		const workspaceData = yield select((state) => state.Organisations.data);

		// If the user has no permissions (not part of any org), logout the user
		if (workspaceData.length <= 0) {
			throw new Error('No Permissions Found');
		}

		// Find the current workspace from the user permissions
		const workspace = workspaceData.find((w) => w.orgID === orgID) || {};

		// Extract the  person profile from Database for the workspace
		const newPersonProfile = yield SuperFetch.get(`/people/${workspace.personID}`, {}, {}, workspace.orgID).then(
			(res) => res.data
		);

		// If the person profile is not found, throw an error
		if (!newPersonProfile.personID) {
			throw new Error('No Person Profile Found');
		}

		// Set the workspace in the local storage
		localStorage.setItem('od', workspace.orgID);

		yield put(
			authActions.switchProfileSuccess({
				selectedProfile: {
					...newPersonProfile,
					roles: workspace.roles,
					orgAlias: workspace.alias
				}
			})
		);
	} catch (error) {
		yield put(authActions.switchProfileError(error.message));
	}
}

export function* switchProfileSuccess() {
	// Do Actions on Switch Profile Success

	// 1: Load all the data resources based on the user's selected Dashboard.
	const selectedDashboard = yield select((state) => state.App?.dashboard?.selected);

	if (selectedDashboard === 'access') {
		// Load the access dashboard data
		yield put(appActions.loadAccessDashboardData());
	} else if (selectedDashboard === 'lockers') {
		// Load the lockers dashboard data
		yield put(appActions.loadLockersDashboardData());
	}
}

export function* signupUser({ payload }) {
	// Do Actions on Signup
	try {
		const { firstName, lastName, email, password, organisationName, mobile } = payload;

		// step 1: Check if the user exists in the system
		const loggedInUser = yield signInWithEmail({ email: email, password: password })
			.then((authUser) => {
				logUserActivityEvent({
					success: true,
					email: email,
					operationType: 'auth/signup'
				});
				return authUser;
			})
			.catch((e) => {
				return e;
			});

		// step 2: Check if the user exist
		if (loggedInUser.user) {
			// If the user exist, but we need to create an workspace for the user

			// step 2.1: create new workspace
			const res = yield setupNewWorkspace({ firstName, lastName, email, organisationName, mobile });

			if (res) {
				// step 2.2: put the success action
				yield put(authActions.signupUserSuccess({ email, password }));
			}

			// step 2.3: return
			return;
		} else if (loggedInUser.code && loggedInUser.code === 'auth/wrong-password') {
			// Step 3: If the user exist, but password is incorrect

			// Step 3.1: throw an error
			throw new Error('We found your account please enter existing password.');
		} else {
			// step 4: if the user does not exist, create a new user and then create a workspace for the user.
			yield signUpWithEmailAndPassword({ email, password })
				.then((newUser) => newUser)
				.catch((e) => {
					throw new Error(e.message);
				});

			// step 4.1: create new workspace
			const res = yield setupNewWorkspace({ firstName, lastName, email, organisationName, mobile });

			if (res) {
				// step 4.2: put the success action
				yield put(authActions.signupUserSuccess({ email, password }));
			}
		}
	} catch (error) {
		yield put(authActions.signupUserError(error.message));
	}
}

export function* signupUserSuccess({ payload }) {
	// Do Actions
	yield put(authActions.login({ email: payload.email, password: payload.password }));
}

export function* signupUserError() {
	// Do Actions

	yield delay(15000); //Reset the error after 15 seconds

	// Reset the error after 15 seconds
	yield put(authActions.resetError());
}

function* exchangeToken(idToken) {
	const baseUrl =
		process.env.NODE_ENV === 'development'
			? `http://${window.location.hostname}:8080`
			: 'https://df.api.zezamii.com';

	const res = yield call(fetch, `${baseUrl}/v4/auth/exchange-token`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ idToken })
	});

	if (!res.ok) {
		throw new Error('Token exchange failed');
	}

	const { data } = yield call([res, res.json]);
	const userCredential = yield call(signInWithCustomToken, data.customToken);
	const newToken = yield call([userCredential.user, userCredential.user.getIdToken]);
	localStorage.setItem('id_token', newToken);
}

/**
 * Checks the authorization by listening to the CHECK_AUTHORIZATION action and validating the token.
 * If the token is valid and the user is logged in, it dispatches the loginSuccess action with the token.
 */
export function* checkAuthorization() {
	try {
		// Check for cross-origin token exchange (e.g. from Rooms Dashboard)
		const url = new URL(window.location.href);
		const incomingToken = url.searchParams.get('token');

		if (incomingToken) {
			url.searchParams.delete('token');
			window.history.replaceState({}, '', url.pathname + url.search);
			yield call(exchangeToken, incomingToken);
			yield put(authActions.loginSuccess());
			return;
		}
	} catch (error) {
		// Token exchange failed — fall through to normal auth check
	}

	const token = getToken().get('idToken');
	const loggedIn = new AuthHelper().checkExpirity(token);

	if (token && loggedIn) {
		yield put(authActions.loginSuccess(token));
	} else {
		yield put(authActions.logout());
	}
}
// The main export for the saga
export default function* authSaga() {
	yield takeLatest(authActions.checkAuthorization, checkAuthorization);
	yield takeLatest(authActions.login, loginRequest);
	yield takeLatest(authActions.loginSuccess, loginSuccess);
	yield takeLatest(authActions.loginError, loginError);
	yield takeLatest(authActions.getUserProfile, getUserProfile);
	yield takeLatest([authActions.getUserProfileSuccess, authActions.switchProfileSuccess], getUserProfileSuccess);
	yield takeLatest(authActions.switchProfileSuccess, switchProfileSuccess);
	yield takeLatest(authActions.logout, logout);
	yield takeLatest(authActions.switchProfile, switchProfile);
	yield takeLatest(authActions.signupUser, signupUser);
	yield takeLatest(authActions.signupUserSuccess, signupUserSuccess);
	yield takeLatest(authActions.signupUserError, signupUserError);
}
